import { apiSlice } from "./apiSlice";
import { ResourceResponse } from "../../types/api/getResources";
import { ResourceHomeworkImpact } from "../../types/api/resourceHomeworkImpact";

export const resourcesApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // The backend exposes no search params usable by this page (the READING
    // kinder/primaria split is client-side only), so the page fetches the
    // full list once (cached under a single entry) and filters client-side.
    // include_programs adds the program(s) each resource belongs to, so
    // duplicated same-title resources can be told apart in the list.
    getResources: builder.query<ResourceResponse[], void>({
      query: () => ({
        url: "/resources",
        method: "GET",
        params: { include_programs: true },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Resource" as const, id })),
              { type: "Resource" as const, id: "LIST" },
            ]
          : [{ type: "Resource" as const, id: "LIST" }],
    }),
    // Inline rename from the list. The backend PATCH is a real partial update
    // (UpdateResourceDto = PartialType(CreateResourceDto)); files and
    // worksheetBlocks are only touched when the payload includes them
    // (Array.isArray guards) and genially_edit_url normalizes undefined to
    // undefined, so sending only the title can never touch anything else.
    updateResourceTitle: builder.mutation<
      ResourceResponse,
      { id: number; title: string }
    >({
      query: ({ id, title }) => ({
        url: `/resources/${id}`,
        method: "PATCH",
        data: { title },
      }),
      // The rename also lands in the audit trail, so the Actividad cache
      // refreshes too. Error guard: a failed rename must not refetch anything.
      invalidatesTags: (result, error, { id }) =>
        error
          ? []
          : [
              { type: "Resource" as const, id },
              { type: "Resource" as const, id: "LIST" },
              { type: "AuditLog" as const, id: "LIST" },
            ],
    }),
    // SCRUM-1020: withdraw-only guard. `confirm` re-sends the delete after
    // the admin resolved a RESOURCE_HAS_HOMEWORKS impact modal;
    // `acknowledgeInFlight` is only required when the impact also reported
    // an IN_PROGRESS attempt. Both ride the query string, not the body — the
    // backend route reads them as query params.
    deleteResource: builder.mutation<
      void,
      { id: number; confirm?: boolean; acknowledgeInFlight?: boolean }
    >({
      query: ({ id, confirm, acknowledgeInFlight }) => ({
        url: `/resources/${id}`,
        method: "DELETE",
        params: {
          ...(confirm ? { confirm: true } : {}),
          ...(acknowledgeInFlight ? { acknowledge_in_flight: true } : {}),
        },
      }),
      // Deleted resources land in the trash, so its cache must refresh too.
      // Error guard: a failed delete must not refetch anything.
      invalidatesTags: (result, error) =>
        error
          ? []
          : [{ type: "Resource" as const, id: "LIST" }, "Trash" as const],
    }),
    // Pre-fetched before a delete click resolves: an empty/zero-count result
    // means the existing direct-delete + undo flow is unaffected.
    getResourceHomeworkImpact: builder.query<ResourceHomeworkImpact[], number>({
      query: (id) => ({
        url: `/resources/${id}/homework-impact`,
        method: "GET",
      }),
    }),
  }),
});

export const {
  useGetResourcesQuery,
  useUpdateResourceTitleMutation,
  useDeleteResourceMutation,
  useLazyGetResourceHomeworkImpactQuery,
} = resourcesApi;
